import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  parseAnalyticsFilters,
  serializeAnalyticsFilters,
  type AnalyticsAvailability,
  type AnalyticsFilters,
  type AnalyticsPeriod,
  type AnalyticsRange,
  type AnalyticsSortField,
  type SortDirection,
} from '@/lib/social-analytics/analyticsFilters';
import type { MediaType } from '@/lib/social-analytics/mediaTypeKey';

/**
 * /analytics filter state, stored in the URL so a view survives reloads and can be shared.
 * Writes use `replace`, so filtering never piles up history entries. Call one setter per event:
 * react-router's functional update reads the params from the last render.
 */
export function useAnalyticsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseAnalyticsFilters(searchParams), [searchParams]);

  const update = useCallback((patch: Partial<AnalyticsFilters>) => {
    setSearchParams((current) => {
      const next: AnalyticsFilters = { ...parseAnalyticsFilters(current), page: 1, ...patch };
      return serializeAnalyticsFilters(next, current);
    }, { replace: true });
  }, [setSearchParams]);

  const setRange = useCallback((range: AnalyticsRange) => update({ range }), [update]);
  const setPlatforms = useCallback((platforms: string[]) => update({ platforms }), [update]);
  const setAccountIds = useCallback((accountIds: string[]) => update({ accountIds }), [update]);
  const setMediaTypes = useCallback((mediaTypes: MediaType[]) => update({ mediaTypes }), [update]);
  const setPeriod = useCallback((period: AnalyticsPeriod) => update({ period }), [update]);
  const setSearch = useCallback((search: string) => update({ search }), [update]);
  const setAvailability = useCallback((availability: AnalyticsAvailability | null) => update({ availability }), [update]);
  const setSort = useCallback((sort: AnalyticsSortField, direction: SortDirection) => update({ sort, direction }), [update]);
  const setPage = useCallback((page: number) => update({ page }), [update]);

  return { filters, setRange, setPlatforms, setAccountIds, setMediaTypes, setPeriod, setSearch, setAvailability, setSort, setPage };
}
